import {
  type unstable_AdapterInterface as AdapterInterface,
  unstable_createAdapterProvider as createAdapterProvider,
  renderQueryString,
  type unstable_UpdateUrlFunction as UpdateUrlFunction,
} from 'nuqs/adapters/custom';
import { ReactNode, useCallback, useMemo } from 'react';
import { Router, useHistory, useLocation } from 'react-router-dom';
import history from './history';

/**
 * Custom React Router v5 adapter for nuqs.
 * This adapter allows nuqs to work with React Router v5's history API.
 */
function useNuqsReactRouterV5Adapter(): AdapterInterface {
  const history = useHistory();
  const location = useLocation();
  const searchParams = useMemo(() => {
    return new URLSearchParams(location.search);
  }, [location.search]);

  const updateUrl = useCallback<UpdateUrlFunction>(
    (search, options) => {
      const queryString = renderQueryString(search);
      if (options.history === 'push') {
        history.push({
          search: queryString,
          hash: window.location.hash,
        });
      } else {
        history.replace({
          search: queryString,
          hash: window.location.hash,
        });
      }
      if (options.scroll) {
        window.scrollTo(0, 0);
      }
    },
    [history.push, history.replace],
  );

  const getSearchParamsSnapshot = useCallback(() => {
    return new URLSearchParams(window.location.search);
  }, []);

  return {
    searchParams,
    updateUrl,
    getSearchParamsSnapshot,
    // Keep pending nuqs queue values until the next push so React does not
    // briefly re-parse an empty URL between queue reset and history.replace
    // (that flash looks like the list filtering twice).
    autoResetQueueOnUpdate: false,
  };
}

/**
 * Nuqs adapter provider component for React Router v5.
 * Wrap components that use nuqs hooks with this provider.
 */
const NuqsAdapter = createAdapterProvider(useNuqsReactRouterV5Adapter);

export function RouterWrapper({ children }: { children: ReactNode }) {
  return (
    <Router history={history}>
      <NuqsAdapter>{children}</NuqsAdapter>
    </Router>
  );
}
